import type { CSSProperties } from "react";
import type { ColumnMetaLite, Row } from "../_components/types";
import {
  ratingMomentumBars,
  scoreTierColor,
  scoreTierVerdictLabel,
} from "@/lib/rating-tiers";

export const HEATMAP_CELL_W = 103;
export const HEATMAP_CELL_H = 60;
export const HEATMAP_CELL_GAP = 10;
/** Floor tile width on mobile / narrow panels. */
export const HEATMAP_CELL_MIN_W = 80;
/** Desktop — never shrink tiles below this; switch to TOP/BOTTOM split instead. */
export const HEATMAP_CELL_MIN_W_DESKTOP = 115;
/**
 * Max tile width. Unbounded: tiles always stretch their 1fr columns to fill the
 * full container width, with no per-tile cap. (Mobile already used Infinity here;
 * desktop now matches so few-column grids reach edge to edge.)
 */
export const HEATMAP_CELL_MAX_W = Number.POSITIVE_INFINITY;
export const HEATMAP_GRID_COLS = 10;
/** Cap the visualization container for short single-grid heatmaps. */
export const HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_5_COLS = 600;
export const HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_6_COLS = 710;
export const HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_7_COLS = 830;
export const HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_8_COLS = 950;
export const HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_9_COLS = 1070;
/** Fallback for 3-4 column grids. */
export const HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_FEW_COLS = 860;

const HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_BY_COLS: Record<number, number> = {
  5: HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_5_COLS,
  6: HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_6_COLS,
  7: HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_7_COLS,
  8: HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_8_COLS,
  9: HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_9_COLS,
};

export function heatmapMinCellWidth(isMobile: boolean): number {
  return isMobile ? HEATMAP_CELL_MIN_W : HEATMAP_CELL_MIN_W_DESKTOP;
}

/** Nominal tile width for column/split layout math (103px on desktop). */
export function heatmapLayoutCellWidth(isMobile: boolean): number {
  return isMobile ? HEATMAP_CELL_MIN_W : HEATMAP_CELL_W;
}

export const TILE_BG = "#1f1f1f";

const FALLBACK_PERCENTAGE_FIELDS = new Set([
  "RevenueGrowthFwd1Y", "Exp3YRevenueGrowthCAGR", "RevGrowthhistCAGR3y", "RevenueFwd5YCAGR",
  "EPSGrowthFY1", "EPSGrowthFY2", "EPSGrowthFY3", "EPSGrowthFY3FY1", "EPShist3Y",
  "EbitdaMargin", "EbitMargin", "NetIncomeMargin", "ROE", "FCFMargin", "Ruleof40", "ROA", "ROCE",
  "EBITDAMarginForecastFY3", "NetIncomeMarginForecastFY3", "DivdendYield", "DivdendYield3Y",
  "Div3Yhistgrowth", "Div3Yexpgrowth", "FCFPayout", "NetIncomePayout", "NetIncomeYield",
  "FCFYield", "BuybackYield", "1weekreturn", "1monthreturn", "3monthreturn", "YTDreturn",
  "1yearreturn", "3yearreturn", "5yearreturn", "10yearReturn", "RealizedVolatility30dAnnualized",
]);

export type HeatmapCellStyle = {
  background: string;
  boxShadow: string;
  color: string;
  border?: string;
  textShadow: string;
  filter: string;
  fontWeight: number;
  colorOpacity: number;
  nameOpacity: number;
  tickerOpacity: number;
  cellScale: number;
  isHero: boolean;
};

export function asNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/** Metrics where 0 is a real value, not missing data. */
const ZERO_IS_VALUE_METRICS = new Set(["Drawdown52Week"]);

/** A row's Bastion score for a metric ("1weekreturnscore"); 0 or missing means no data behind it. */
function rowMetricScore(row: Record<string, unknown>, metric: string): number | null {
  return asNum(row[`${metric}score`] ?? row[`${metric}Score`]);
}

/**
 * Most metrics store a missing value as 0. A real zero still carries a score
 * (Charles Schwab closed two Fridays running at $96.70: 1-week return 0, score 5),
 * so with the row at hand a scored zero counts as a value. 52-week drawdown is
 * 0 when price is at the high.
 */
export function isPresentHeatmapMetricValue(
  metric: string,
  raw: number | null,
  row?: Record<string, unknown> | null,
): boolean {
  if (raw === null || Number.isNaN(raw) || !Number.isFinite(raw)) return false;
  if (raw === 0 && !ZERO_IS_VALUE_METRICS.has(metric)) {
    const score = row ? rowMetricScore(row, metric) : null;
    return score != null && score > 0;
  }
  return true;
}

export function isMetricPercent(metric: string, columnMeta: Record<string, ColumnMetaLite>): boolean {
  const meta = columnMeta[metric]?.format;
  if (meta?.type === "percent") return true;
  if (!meta?.type && FALLBACK_PERCENTAGE_FIELDS.has(metric)) return true;
  return false;
}

export function formatHeatmapValue(
  value: number | null,
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
  /** The zero is a real value (see isPresentHeatmapMetricValue): print it, not a dash. */
  zeroIsValue = false,
): string {
  if (value === null) return "";
  const meta = columnMeta[metric]?.format;
  if (meta?.zeroAsDash && value === 0 && !zeroIsValue) return "";

  if (isMetricPercent(metric, columnMeta)) {
    const decimals = meta?.decimals ?? 0;
    const rounded = Number((value * 100).toFixed(decimals));
    // Sign from the printed number: 0.3% and -0.2% both read "0%", not "+0%" / "-0%".
    if (rounded === 0) return `${(0).toFixed(decimals)}%`;
    const sign = meta?.signed && rounded > 0 ? "+" : "";
    return `${sign}${rounded.toFixed(decimals)}%`;
  }

  if (meta?.type === "number" || meta?.type === "integer" || meta?.type === "ratio") {
    const decimals = meta?.decimals ?? (meta.type === "ratio" ? 2 : 0);
    return value.toFixed(decimals);
  }
  return String(Math.round(value));
}

function extractRgb(color: string): [number, number, number] {
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    return [
      parseInt(hex.substring(0, 2), 16),
      parseInt(hex.substring(2, 4), 16),
      parseInt(hex.substring(4, 6), 16),
    ];
  }
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    return [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])];
  }
  return [0, 0, 0];
}

export function getStrategyScoreColor(scoreValue: number | null | undefined): string {
  if (scoreValue == null || !isFinite(scoreValue)) return "#911C13";
  const s = Math.max(0, Math.min(100, Number(scoreValue)));
  if (s >= 90) return "#2A4F15";
  if (s >= 80) return "#3A5E05";
  if (s >= 70) return "#506704";
  if (s >= 58) return "#978601";
  if (s >= 46) return "#C3990A";
  if (s >= 34) return "#B7802B";
  if (s >= 23) return "#AB5F22";
  if (s >= 11) return "#A0401A";
  return "#911C13";
}

/**
 * Map a 0–100 strategy rating to a 1–10 metric score using letter-grade
 * boundaries from stock/format.ts getStrategyColor (A+ … C-).
 */
export function strategyRatingToMetricScore(rating: number): number {
  const s = Math.max(0, Math.min(100, Number(rating)));
  if (s >= 90) return 10;
  if (s >= 80) return 9;
  if (s >= 70) return 8;
  if (s >= 58) return 7;
  if (s >= 46) return 6;
  if (s >= 34) return 5;
  if (s >= 23) return 4;
  if (s >= 11) return 3;
  if (s >= 1) return 2;
  return 1;
}

function clampStrategyRating(rating: number): number {
  return Math.max(0, Math.min(100, Number(rating)));
}

/** Strategy rating tier on 0–100: ≥67 green, ≥34 yellow, else red (canon A). */
export function strategyRatingTierTextColor(rating: number): string {
  const s = clampStrategyRating(rating);
  if (s <= 0) return scoreTierColor(null);
  return scoreTierColor(s);
}

/** Map 0–100 strategy rating to 0–10 heat tint (preserves gradient inside each tier). */
export function strategyRatingToHeatScore10(rating: number): number {
  const s = clampStrategyRating(rating);
  if (s >= 67) return 7 + ((s - 67) / 33) * 3;
  if (s >= 34) return 4 + ((s - 34) / 32) * 2;
  if (s >= 1) return 1 + ((s - 1) / 32) * 2;
  return 1;
}

/** Verdict label for strategy rating columns (Title Case, canon A). */
export function strategyRatingVerdictLabel(rating: number): string {
  const label = scoreTierVerdictLabel(rating);
  return label || "Very Weak";
}

/** 3-bar momentum indicator — canon A bands, ≥85 always fills 3. */
export function strategyRatingMomentumBars(rating: number): { filled: number; color: string } {
  return ratingMomentumBars(rating);
}

/** Solid bar/dot accent from a 1–10 metric score (matches heatmapStyleByScore tiers). */
export function metricScoreBarColor(score: number): string {
  const s = Math.max(1, Math.min(10, Math.round(score)));
  if (s >= 10) return "#166534";
  if (s >= 9) return "#16A34A";
  if (s >= 7) return "#4ADE80";
  if (s >= 4) return "#EAB308";
  if (s >= 3) return "#F87171";
  if (s >= 2) return "#DC2626";
  return "#991B1B";
}

/** Flat accent colours for chevrons / Δ rating on dark cells (one green, one yellow, one red). */
export const HEAT_ACCENT_GREEN_ON_DARK = "#4ADE80";
export const HEAT_ACCENT_YELLOW_ON_DARK = "#EAB308";
export const HEAT_ACCENT_RED_ON_DARK = "#F87171";

/** Chevron accent on dark table cells — flat tier colour, no score gradients. */
export function metricScoreChevronColorOnDark(score: number): string {
  const s = Math.max(1, Math.min(10, Math.round(score)));
  if (s >= 7) return HEAT_ACCENT_GREEN_ON_DARK;
  if (s >= 4) return HEAT_ACCENT_YELLOW_ON_DARK;
  return HEAT_ACCENT_RED_ON_DARK;
}

/**
 * Solid heatmap tile score for 0–100 strategy ratings.
 * Shade follows the 1–3 bar count (same rules as MomentumBars).
 * Green tier maps to three distinct heatmap stops: 10 dark, 9 medium, 7 light.
 */
export function strategyRatingSolidHeatScore(rating: number): number {
  const s = clampStrategyRating(rating);
  const { filled } = strategyRatingMomentumBars(s);

  if (s >= 67) {
    if (filled >= 3) return 10;
    if (filled >= 2) return 9;
    return 7;
  }
  if (s >= 34) {
    if (filled >= 3) return 6;
    if (filled >= 2) return 5;
    return 4;
  }
  if (filled >= 3) return 3;
  if (filled >= 2) return 2;
  return 1;
}

export type HeatFillScheme = "tint" | "solid" | "minimal";

const HEAT_SOLID_CHEVRON_COLOR = "#ffffff";

/** Chevron colour in solid heatmap table mode — flat white on dark cells. */
export function heatmapMetricAccentOnDark(
  score10: number | null | undefined,
  fillScheme: HeatFillScheme,
): string | undefined {
  if (fillScheme !== "solid" || score10 == null || !Number.isFinite(Number(score10))) return undefined;
  return HEAT_SOLID_CHEVRON_COLOR;
}

export function strategyChevronAccentOnDark(
  rating: number | null | undefined,
  fillScheme: HeatFillScheme,
): string | undefined {
  if (fillScheme !== "solid" || rating == null || !Number.isFinite(Number(rating)) || Number(rating) === 0) {
    return undefined;
  }
  return HEAT_SOLID_CHEVRON_COLOR;
}

export function ratingDeltaAccentOnDark(
  delta: number | null | undefined,
  fillScheme: HeatFillScheme,
): string | undefined {
  if (fillScheme !== "solid" || delta == null || !Number.isFinite(Number(delta))) return undefined;
  const rounded = Math.round(Number(delta));
  if (rounded === 0) return undefined;
  return rounded > 0 ? HEAT_ACCENT_GREEN_ON_DARK : HEAT_ACCENT_RED_ON_DARK;
}

export function isStrategyMetric(metric: string): boolean {
  return [
    "PureGrowth", "PureQuality", "PureValue", "ValueMomentum",
    "PureMomentum", "GrowthMomentum", "GEAR5FScore",
    "QualityGrowth", "PureAntifragility",
  ].includes(metric);
}

export function isMarketCapMetric(metric: string): boolean {
  return metric === "MarketCap";
}

/** Panel header badge when a Top/Bottom market-cap slice is active. */
export function formatMarketCapHeaderLabel(
  mode: "top" | "bottom",
  topByMarketCap: string,
  stocksCount: number | null,
): string {
  if (!topByMarketCap) return "";
  const count = stocksCount || parseInt(topByMarketCap, 10);
  if (!count) return "";
  const prefix = mode === "bottom" ? "Bottom" : "Top";
  return `${prefix} ${count} by MCap`;
}

export function scoreFieldFor(keys: string[], base: string): string | null {
  if (!keys?.length || !base) return null;

  const exactMatches = [
    `${base}score`,
    `${base}Score`,
    `${base}_score`,
    `${base}_Score`,
    `${base} Score`,
    `${base}Rating`,
    `${base}_Rating`,
    `${base}Rank`,
    `${base}_Rank`,
    `${base}ScoreValue`,
    `${base}_ScoreValue`,
  ];
  for (const k of exactMatches) {
    if (keys.includes(k)) return k;
  }

  const baseN = base.toLowerCase().replace(/[^a-z0-9]/g, "");
  const pair = keys.find((k) => {
    const n = k.toLowerCase().replace(/[^a-z0-9]/g, "");
    return (
      n.includes(baseN) &&
      (n.includes("score") || n.includes("rating") || n.includes("rank"))
    );
  });
  if (pair) return pair;

  return (
    keys.find((k) => k.toLowerCase().replace(/[^a-z0-9]/g, "").includes("earningsqualityscore")) ??
    keys.find((k) => k.toLowerCase().endsWith("score")) ??
    null
  );
}


type Rgb3 = [number, number, number];

/**
 * Three-stop ramps per group on the dark field: muted (close to the card, for
 * values near the yellow band) → mid → deep saturated extreme. A wide lightness
 * and saturation spread, while the strongest tile stays dark enough for white text.
 */
const RAMP_GREEN: [Rgb3, Rgb3, Rgb3] = [
  [40, 70, 52],
  [30, 108, 64],
  [23, 150, 78],
];
const RAMP_RED: [Rgb3, Rgb3, Rgb3] = [
  [82, 46, 46],
  [142, 48, 48],
  [204, 52, 52],
];
export const RAMP_YELLOW: Rgb3 = [132, 108, 31];

/** Colour at t ∈ [0, 1] on a group ramp (0 = softest, 1 = strongest). */
export function scoreRampRgb(group: "green" | "red", t: number): Rgb3 {
  const [a, b, c] = group === "green" ? RAMP_GREEN : RAMP_RED;
  const x = Math.max(0, Math.min(1, t));
  const [from, to, k] = x < 0.5 ? [a, b, x / 0.5] : [b, c, (x - 0.5) / 0.5];
  return from.map((v, i) => Math.round(v + (to[i] - v) * k)) as Rgb3;
}

/** 1–10 score → opaque tile colour on the shared ramps (10 / 1 strongest). */
function scoreTileRgb(sc: number): Rgb3 {
  if (sc >= 10) return scoreRampRgb("green", 1);
  if (sc >= 9) return scoreRampRgb("green", 0.66);
  if (sc >= 8) return scoreRampRgb("green", 0.33);
  if (sc >= 7) return scoreRampRgb("green", 0);
  if (sc >= 4) return RAMP_YELLOW;
  if (sc >= 3) return scoreRampRgb("red", 0.2);
  if (sc >= 2) return scoreRampRgb("red", 0.6);
  return scoreRampRgb("red", 1);
}

export function heatmapStyleByScore(score?: number | null): HeatmapCellStyle {
  if (score == null || Number.isNaN(score)) {
    return {
      background: TILE_BG,
      boxShadow: "none",
      color: "#ADADAD",
      border: undefined,
      textShadow: "none",
      filter: "none",
      colorOpacity: 1.0,
      nameOpacity: 1.0,
      tickerOpacity: 1.0,
      cellScale: 1.0,
      isHero: false,
    };
  }

  const s = Math.max(1, Math.min(10, Math.round(score)));
  // Dark-field rule: strength = distance from neutral grey, so extremes are the
  // brightest, most saturated tiles and scores near the yellow band fade toward
  // grey. Same palette as the Poster views (HeatmapSocialBoard).
  const [r, g, b] = scoreTileRgb(s);
  return {
    background: `rgb(${r}, ${g}, ${b})`,
    color: "#ffffff",
    colorOpacity: 1.0,
    nameOpacity: 1.0,
    tickerOpacity: 1.0,
    border: `1px solid rgba(${r}, ${g}, ${b}, 0.3)`,
    textShadow: "none",
    boxShadow: "none",
    filter: "none",
    fontWeight: 700,
    cellScale: 1.0,
    isHero: false,
  };
}

export function heatmapStyleByStrategyScore(rating?: number | null): HeatmapCellStyle {
  if (rating == null || Number.isNaN(rating) || rating === 0) {
    return heatmapStyleByScore(null);
  }
  return heatmapStyleByScore(strategyRatingToMetricScore(rating));
}

function heatmapStyleByMarketCap(value: number, min: number, max: number): HeatmapCellStyle {
  if (min === max) return heatmapStyleByScore(null);

  const normalized = (value - min) / (max - min);
  const GREEN_COLOR = "#4ade80";
  const [r, g, b] = extractRgb(GREEN_COLOR);
  const bgTopOpacity = 0.1 + normalized * 0.2;
  const bgBottomOpacity = Math.max(0, bgTopOpacity - 0.04);
  const borderOpacity = normalized > 0.8 ? 0.2 : normalized > 0.5 ? 0.1 : 0.0;

  return {
    background: `linear-gradient(135deg, rgba(${r}, ${g}, ${b}, ${bgTopOpacity}), rgba(${r}, ${g}, ${b}, ${bgBottomOpacity}))`,
    color: GREEN_COLOR,
    colorOpacity: 1.0,
    nameOpacity: 1.0,
    tickerOpacity: 1.0,
    border: `1px solid rgba(${r}, ${g}, ${b}, ${borderOpacity})`,
    textShadow: "none",
    boxShadow: "none",
    filter: "none",
    fontWeight: 700,
    cellScale: normalized > 0.8 ? 1.01 : 1.0,
    isHero: normalized > 0.9,
  };
}

export function buildFinancialsScoreMap(rows: Row[], metricKey: string): Map<string, number> | null {
  const vals: { ticker: string; v: number }[] = [];
  for (const row of rows) {
    const v = asNum(row[metricKey]);
    if (v !== null && !isNaN(v) && isFinite(v) && v !== 0) {
      vals.push({ ticker: row.FullTicker as string, v });
    }
  }
  if (vals.length === 0) return null;

  const positives = vals.filter((x) => x.v > 0).sort((a, b) => a.v - b.v);
  const negatives = vals.filter((x) => x.v < 0).sort((a, b) => a.v - b.v);

  const pctile = (sorted: typeof positives, p: number) => {
    if (sorted.length === 0) return 0;
    const idx = (p / 100) * (sorted.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return sorted[lo].v;
    return sorted[lo].v + (sorted[hi].v - sorted[lo].v) * (idx - lo);
  };

  const map = new Map<string, number>();

  if (positives.length > 0) {
    const maxVal = positives[positives.length - 1].v;
    const p50 = pctile(positives, 50);
    const p25 = pctile(positives, 25);
    const ratio = maxVal > 0 ? pctile(positives, 75) / maxVal : 1;
    let darkPct: number;
    if (ratio < 0.1) darkPct = 97;
    else if (ratio < 0.3) darkPct = 90;
    else if (ratio < 0.5) darkPct = 85;
    else darkPct = 75;
    const darkThreshold = pctile(positives, darkPct);

    for (const item of positives) {
      let score: number;
      if (item.v >= darkThreshold) score = 10;
      else if (item.v >= p50) score = 9;
      else if (item.v >= p25) score = 7;
      else score = 5;
      map.set(item.ticker, score);
    }
  }

  for (let i = 0; i < negatives.length; i++) {
    const pct = negatives.length > 1 ? i / (negatives.length - 1) : 0.5;
    let score: number;
    if (pct <= 0.33) score = 1;
    else if (pct <= 0.67) score = 2;
    else score = 3;
    map.set(negatives[i].ticker, score);
  }

  return map;
}

/**
 * Return-category levels that are not signed P&L.
 * Both are 0–100 positions in a range. Split uses the red/green tiers, not the yellow return band.
 */
const LEVEL_NOT_SIGNED_RETURN_METRICS = new Set([
  "Priceof52WeekHigh",
  "Price52WeekRange",
  "Drawdown52Week",
]);

/** Signed-return color bands (green / yellow / red by sign). Level metrics are excluded. */
export function isSignedReturnColorMetric(
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
): boolean {
  if (LEVEL_NOT_SIGNED_RETURN_METRICS.has(metric)) return false;
  return columnMeta[metric]?.category === "Return";
}

function resolveMetricScore(
  row: Row,
  keys: string[],
  metricKey: string,
  finScoreMap: Map<string, number> | null,
): number | null {
  if (finScoreMap) {
    return finScoreMap.get(row.FullTicker as string) ?? null;
  }
  if (isStrategyMetric(metricKey) || isMarketCapMetric(metricKey)) {
    return asNum(row[metricKey]);
  }
  const scoreKey = scoreFieldFor(keys, metricKey);
  return scoreKey ? asNum(row[scoreKey]) : null;
}

/**
 * The 0–10 score behind a tile's green / yellow / red tier, as the main grid
 * resolves it (strategy ratings mapped, negative returns capped at red).
 * Null when the metric has no score (market cap) or the row has none.
 */
export function resolveHeatmapRowScore10(
  row: Row,
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
  rows: Row[],
  finScoreMap: Map<string, number> | null,
): number | null {
  if (isMarketCapMetric(metric)) return null;
  const keys = rows[0] ? Object.keys(rows[0]) : [];
  const score = resolveMetricScore(row, keys, metric, finScoreMap);
  if (score == null || Number.isNaN(score)) return null;
  if (isStrategyMetric(metric)) return score === 0 ? null : strategyRatingToMetricScore(score);
  const value = asNum(row[metric]);
  return isSignedReturnColorMetric(metric, columnMeta) && value != null && value < 0
    ? Math.min(score, 3)
    : score;
}

/**
 * Poster colouring, shared by the Poster view and the Classic grid: three score
 * groups (green >= 7, yellow 4-6, red < 4), and inside a group a smooth ramp
 * toward its extreme, so neighbouring values get neighbouring shades (no bands
 * by position) and weak tiles go darker, never greyer. Null = no score.
 */
export function posterScoreFills(items: { value: number; score: number | null }[]): (string | null)[] {
  const fills: (string | null)[] = items.map(() => null);
  const scored = items.map((r, i) => ({ i, r })).filter((x) => x.r.score != null);
  if (scored.length === 0) return fills;
  // Higher value = better when value and score move together (P/E, drawdown flip it).
  const meanV = scored.reduce((a, x) => a + x.r.value, 0) / scored.length;
  const meanS = scored.reduce((a, x) => a + (x.r.score as number), 0) / scored.length;
  const cov = scored.reduce((a, x) => a + (x.r.value - meanV) * ((x.r.score as number) - meanS), 0);
  const goodness = (v: number) => (cov >= 0 ? v : -v);
  const tierOf = (score: number) => (score >= 7 ? "green" : score >= 4 ? "yellow" : "red");
  const rgb = (c: Rgb3) => `rgb(${c.join(",")})`;
  const groups: Record<"green" | "yellow" | "red", typeof scored> = { green: [], yellow: [], red: [] };
  for (const x of scored) groups[tierOf(x.r.score as number)].push(x);
  (Object.keys(groups) as ("green" | "yellow" | "red")[]).forEach((tier) => {
    // Weakest → strongest within the group.
    const list = [...groups[tier]].sort((a, b) =>
      tier === "red" ? goodness(b.r.value) - goodness(a.r.value) : goodness(a.r.value) - goodness(b.r.value),
    );
    // Position = half rank, half value: rank spreads the shades so neighbours
    // differ; value lets an outlier take the strongest shade alone. Value runs
    // from the group's weakest to its 90th percentile, so one outlier does not
    // flatten the rest.
    const n = list.length;
    // Strength toward the group's own end: better for green, worse for red.
    // Not |value|: on drawdown the best green is near 0 and |value| would run
    // against the rank, flattening every green to one shade.
    const strengthOf = (v: number) => (tier === "red" ? -goodness(v) : goodness(v));
    const strengths = list.map((x) => strengthOf(x.r.value));
    const lo = Math.min(...strengths);
    const sorted = [...strengths].sort((a, b) => a - b);
    const hi = sorted[Math.min(n - 1, Math.floor((n - 1) * 0.9))] ?? lo;
    list.forEach((x, k) => {
      if (tier === "yellow") {
        fills[x.i] = rgb(RAMP_YELLOW);
        return;
      }
      const pRank = n > 1 ? k / (n - 1) : 1;
      const pValue = hi > lo ? Math.min(1, Math.max(0, (strengthOf(x.r.value) - lo) / (hi - lo))) : 1;
      fills[x.i] = rgb(scoreRampRgb(tier, 0.5 * pRank + 0.5 * pValue));
    });
  });
  return fills;
}

/** Poster fills for the tiles a grid shows, keyed by FullTicker; null when the metric has no scores. */
export function buildPosterFillMap(
  shownRows: Row[],
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
  scorePool: Row[],
  finScoreMap: Map<string, number> | null,
): Map<string, string> | null {
  const items: { key: string; value: number; score: number | null }[] = [];
  for (const row of shownRows) {
    const value = asNum(row[metric]);
    if (value === null || !isPresentHeatmapMetricValue(metric, value, row)) continue;
    items.push({
      key: String(row.FullTicker ?? row.Ticker ?? ""),
      value,
      score: resolveHeatmapRowScore10(row, metric, columnMeta, scorePool, finScoreMap),
    });
  }
  if (!items.some((x) => x.score != null)) return null;
  const fills = posterScoreFills(items);
  const map = new Map<string, string>();
  items.forEach((x, i) => {
    const fill = fills[i];
    if (fill && x.key) map.set(x.key, fill);
  });
  return map;
}

/** A Classic tile in its Poster fill (white text, faint border in the same colour). */
export function withPosterFill<T extends Pick<HeatmapCellStyle, "background" | "color">>(style: T, fill: string | undefined): T {
  if (!fill) return style;
  return {
    ...style,
    background: fill,
    border: `1px solid ${fill.replace(/^rgb\((.*)\)$/, "rgba($1, 0.3)")}`,
    color: "#ffffff",
  };
}

/** Same cell colors as the main Custom heatmap (non-split, full values). */
export function resolveHeatmapCellStyle(
  row: Row,
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
  rows: Row[],
  finScoreMap: Map<string, number> | null,
): HeatmapCellStyle {
  const keys = rows[0] ? Object.keys(rows[0]) : [];
  const rawValue = asNum(row[metric]);
  const isPercent = isMetricPercent(metric, columnMeta);
  const value = rawValue !== null && isPercent ? rawValue / 100 : rawValue;
  const hasValidValue = isPresentHeatmapMetricValue(metric, rawValue, row);
  const isReturnMetric = isSignedReturnColorMetric(metric, columnMeta);
  const isStrategy = isStrategyMetric(metric);
  const isMarketCap = isMarketCapMetric(metric);

  const score = resolveMetricScore(row, keys, metric, finScoreMap);

  if (hasValidValue && isMarketCap && value != null) {
    const caps = rows
      .map((r) => asNum(r.MarketCap))
      .filter((v): v is number => v !== null);
    const min = caps.length ? Math.min(...caps) : 0;
    const max = caps.length ? Math.max(...caps) : 1;
    return heatmapStyleByMarketCap(value, min, max);
  }

  if (hasValidValue && score != null) {
    const isNegativeReturn = isReturnMetric && value != null && value < 0;
    const effectiveScore = isNegativeReturn ? Math.min(score, 3) : score;
    return isStrategy
      ? heatmapStyleByStrategyScore(effectiveScore)
      : heatmapStyleByScore(effectiveScore);
  }

  if (hasValidValue) {
    return {
      background: TILE_BG,
      boxShadow: "none",
      color: "#F5F5F5",
      border: undefined,
      textShadow: "none",
      filter: "none",
      fontWeight: 400,
      colorOpacity: 1.0,
      nameOpacity: 1.0,
      tickerOpacity: 1.0,
      cellScale: 1.0,
      isHero: false,
    };
  }

  return heatmapStyleByScore(null);
}

export type HeatmapSplitColorContext = {
  /** True for the left physical block (first halfLen rows), not the TOP label. */
  isTopBlock: boolean;
  /** 0-based index within the current half block. */
  posInHalf: number;
  halfLen: number;
  /** Whether the left block shows the high-ranked ("good") half. */
  leftIsGood: boolean;
  /** Pre-ranked Return scores — absolute value scale (greens / yellows / reds). */
  returnScoreMap?: Map<string, number> | null;
};

/**
 * Return-metric split greens for the *good* half (TOP when sorted desc).
 * Positives and negatives use equal tertiles so the good side gets 3 greens
 * (10 / 9 / 7) when that sign exists in view. BOTTOM positives use yellow
 * tiers in {@link resolveHeatmapSplitCellStyle}, not this map.
 */
export function buildSplitReturnScoreMap(
  rows: Row[],
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
  halfLen: number,
): Map<string, number> | null {
  if (!isSignedReturnColorMetric(metric, columnMeta)) return null;
  if (halfLen <= 0 || rows.length < halfLen * 2) return null;

  const visible = [
    ...rows.slice(0, halfLen),
    ...rows.slice(rows.length - halfLen),
  ];

  const positives: { ticker: string; v: number }[] = [];
  const negatives: { ticker: string; v: number }[] = [];
  for (const row of visible) {
    const v = asNum(row[metric]);
    const ticker = String(row.FullTicker ?? "");
    if (!ticker || v === null || !Number.isFinite(v) || v === 0) continue;
    if (v > 0) positives.push({ ticker, v });
    else negatives.push({ ticker, v });
  }
  if (positives.length === 0 && negatives.length === 0) return null;

  // Ascending: weakest → strongest for +, most-negative → least-negative for −.
  positives.sort((a, b) => a.v - b.v);
  negatives.sort((a, b) => a.v - b.v);

  const map = new Map<string, number>();

  // Same tertile cut for both signs. Greens: 7 light / 9 mid / 10 dark.
  // Reds: 3 light / 2 mid / 1 dark.
  for (let i = 0; i < positives.length; i++) {
    const pct = positives.length > 1 ? i / (positives.length - 1) : 0.5;
    let score: number;
    if (pct <= 0.33) score = 7;
    else if (pct <= 0.67) score = 9;
    else score = 10;
    map.set(positives[i].ticker, score);
  }

  for (let i = 0; i < negatives.length; i++) {
    const pct = negatives.length > 1 ? i / (negatives.length - 1) : 0.5;
    let score: number;
    if (pct <= 0.33) score = 1;
    else if (pct <= 0.67) score = 2;
    else score = 3;
    map.set(negatives[i].ticker, score);
  }

  return map;
}

/**
 * TOP/BOTTOM split coloring for Return metrics:
 * TOP positives → 3 greens by rank in the half (10 / 9 / 7),
 * BOTTOM positives → yellow band,
 * BOTTOM negatives → 3 reds by rank among negatives only (3 / 2 / 1).
 * Red/green metrics (not signed returns) use three positional tiers on each half:
 * greens 10 / 9 / 7, reds 3 / 2 / 1. Price vs 52w high and 52w Range Ratio are in this group.
 */
export function resolveHeatmapSplitCellStyle(
  row: Row,
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
  rows: Row[],
  finScoreMap: Map<string, number> | null,
  split: HeatmapSplitColorContext,
): HeatmapCellStyle {
  const keys = rows[0] ? Object.keys(rows[0]) : [];
  const rawValue = asNum(row[metric]);
  const hasValidValue = isPresentHeatmapMetricValue(metric, rawValue, row);

  if (!hasValidValue) return heatmapStyleByScore(null);

  const isReturnMetric = isSignedReturnColorMetric(metric, columnMeta);
  const isStrategy = isStrategyMetric(metric);
  const dbScore = resolveMetricScore(row, keys, metric, finScoreMap);
  const { isTopBlock, posInHalf, halfLen, leftIsGood } = split;
  const isLeftBlock = isTopBlock;
  const isGoodHalf = leftIsGood ? isLeftBlock : !isLeftBlock;

  if (isStrategy) {
    return dbScore != null
      ? heatmapStyleByStrategyScore(dbScore)
      : heatmapStyleByScore(null);
  }

  const styleFromScore = (score: number) => heatmapStyleByScore(score);

  const t1 = Math.max(1, Math.ceil(halfLen / 3));
  const t2 = Math.max(t1 + 1, Math.ceil((2 * halfLen) / 3));
  const reversePosInHalf =
    !leftIsGood && (isGoodHalf ? !isLeftBlock : isLeftBlock);
  const effectivePosInHalf = reversePosInHalf
    ? halfLen - 1 - posInHalf
    : posInHalf;

  /** Three distinct green stops (8 shares the same hex as 7 in heatmapStyleByScore). */
  const positionalGreenTiers = (): number =>
    effectivePosInHalf < t1 ? 10 : effectivePosInHalf < t2 ? 9 : 7;

  const positionalRedTiers = (): number =>
    effectivePosInHalf < t1 ? 3 : effectivePosInHalf < t2 ? 2 : 1;

  /** Good-half negatives stay pale — dark reds reserved for BOTTOM. */
  if (isReturnMetric && isGoodHalf && rawValue != null && rawValue < 0) {
    const score = effectivePosInHalf < t1 ? 4 : 3;
    return styleFromScore(score);
  }

  /** TOP half positives: 3 greens by rank within the half. */
  if (isReturnMetric && isGoodHalf && rawValue != null && rawValue > 0) {
    return styleFromScore(positionalGreenTiers());
  }

  /** BOTTOM half positives: yellow band (weak +, not red). */
  if (isReturnMetric && !isGoodHalf && rawValue != null && rawValue > 0) {
    const score = effectivePosInHalf < t1 ? 6 : effectivePosInHalf < t2 ? 5 : 4;
    return styleFromScore(score);
  }

  /**
   * BOTTOM half negatives: 3 reds ranked among negatives only.
   * Ranking by position in the full half collapses every red into the last
   * tertile when negatives occupy only the trailing columns.
   */
  if (isReturnMetric && !isGoodHalf && rawValue != null && rawValue < 0) {
    const halfRows = isTopBlock
      ? rows.slice(0, halfLen)
      : rows.slice(Math.max(0, rows.length - halfLen));
    // Rank by value, not by tile position: with "lowest first" the worst stocks
    // lead the block, and a positional rank would paint them the softest red.
    const negValues: number[] = [];
    for (let i = 0; i < halfRows.length; i++) {
      const v = asNum(halfRows[i][metric]);
      if (v !== null && Number.isFinite(v) && v < 0) negValues.push(v);
    }
    const n = negValues.length;
    if (n > 0) {
      negValues.sort((a, b) => b - a); // least bad → worst
      const negIdx = negValues.indexOf(rawValue);
      const pct = n > 1 ? Math.max(0, negIdx) / (n - 1) : 0.5;
      // Least bad → softest red (3), worst → strongest red (1).
      const score = pct <= 0.33 ? 3 : pct <= 0.67 ? 2 : 1;
      return styleFromScore(score);
    }
    return styleFromScore(positionalRedTiers());
  }

  if (isGoodHalf) {
    return styleFromScore(positionalGreenTiers());
  }

  return styleFromScore(positionalRedTiers());
}

export function isFinancialsMetricCategory(
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
): boolean {
  const cat = columnMeta[metric]?.category;
  return cat === "Financials" || cat === "Forecast";
}

export type PrepareHeatmapStockPoolOptions = {
  hideNA?: boolean;
  topByMarketCap?: string;
  stocksCount?: number | null;
  marketCapLimitMode?: "top" | "bottom";
};

/** Pre-sort pool: optional MCap cap + Hide NA — same rules as Custom heatmap. */
export function prepareHeatmapStockPool(
  stocks: Row[],
  metric: string,
  options: PrepareHeatmapStockPoolOptions = {},
): Row[] {
  const {
    hideNA = false,
    topByMarketCap = "",
    stocksCount = null,
    marketCapLimitMode = "top",
  } = options;
  const parsedCap = topByMarketCap ? parseInt(topByMarketCap, 10) : NaN;
  const limitCount =
    stocksCount !== null && stocksCount > 0
      ? stocksCount
      : Number.isFinite(parsedCap) && parsedCap > 0
        ? parsedCap
        : null;
  const hasLimit = limitCount !== null;
  const poolLimit = hasLimit ? limitCount! : stocks.length;

  const sortedByCap = [...stocks].sort((a, b) => {
    const av = asNum(a.MarketCap);
    const bv = asNum(b.MarketCap);
    return marketCapLimitMode === "bottom"
      ? (av ?? Number.POSITIVE_INFINITY) - (bv ?? Number.POSITIVE_INFINITY)
      : (bv ?? -1) - (av ?? -1);
  });

  const isValidMetricValue = (row: Row) =>
    isPresentHeatmapMetricValue(metric, asNum(row[metric]), row);

  if (hideNA && hasLimit) {
    const result: Row[] = [];
    for (const row of sortedByCap) {
      if (result.length >= poolLimit) break;
      if (isValidMetricValue(row)) result.push(row);
    }
    return result;
  }
  if (hideNA) return sortedByCap.filter(isValidMetricValue);
  if (hasLimit) return sortedByCap.slice(0, poolLimit);
  return sortedByCap;
}

/** Sort pool by metric value (invalid/zero values sink to the end). */
export function sortHeatmapPoolByMetric(
  pool: Row[],
  metric: string,
  sortAscending = false,
): Row[] {
  const dir = sortAscending ? 1 : -1;
  return [...pool].sort((a, b) => {
    const aValue = asNum(a[metric]);
    const bValue = asNum(b[metric]);
    const aValid = isPresentHeatmapMetricValue(metric, aValue, a);
    const bValid = isPresentHeatmapMetricValue(metric, bValue, b);
    if (aValid && bValid) return dir * ((aValue as number) - (bValue as number));
    if (aValid && !bValid) return -1;
    if (!aValid && bValid) return 1;
    return 0;
  });
}

export type HeatmapDisplayRowsResult = {
  rows: Row[];
  totalPoolSize: number;
  isSplitView: boolean;
  isFinancialsCategory: boolean;
};

/**
 * After sorting: ≤100 rows in one grid, or top-50 + bottom-50 when pool > 100
 * (skipped for Financials / Forecast metrics — same as Custom heatmap).
 */
export function sliceHeatmapDisplayRows(
  sortedPool: Row[],
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
  customRowCount = 0,
): HeatmapDisplayRowsResult {
  const totalPoolSize = sortedPool.length;
  const realPoolSize = totalPoolSize - customRowCount;
  const isFinancialsCategory = isFinancialsMetricCategory(metric, columnMeta);
  const isSplitView = realPoolSize > 100 && !isFinancialsCategory;
  let rows: Row[];
  if (isSplitView) {
    rows = [...sortedPool.slice(0, 50), ...sortedPool.slice(totalPoolSize - 50)];
  } else {
    rows = sortedPool.slice(0, 100);
  }
  return { rows, totalPoolSize: realPoolSize, isSplitView, isFinancialsCategory };
}

/** Whether the left column in split view shows the “good” (top) half. */
export function heatmapSplitLeftIsGood(
  displayRows: Row[],
  metric: string,
  sortAscending = false,
): boolean {
  if (displayRows.length < 50) return !sortAscending;
  const keys = displayRows[0] ? Object.keys(displayRows[0]) : [];
  if (isStrategyMetric(metric) || isMarketCapMetric(metric)) return !sortAscending;
  const scoreKey = scoreFieldFor(keys, metric);
  if (!scoreKey) return !sortAscending;
  const sample = (slice: Row[]) =>
    slice
      .map((s) => asNum(s[scoreKey]))
      .filter((v): v is number => v !== null && !isNaN(v) && v !== 0);
  const leftScores = sample(displayRows.slice(0, 10));
  const rightScores = sample(displayRows.slice(displayRows.length - 10));
  if (leftScores.length === 0 || rightScores.length === 0) return !sortAscending;
  const avgLeft = leftScores.reduce((a, b) => a + b, 0) / leftScores.length;
  const avgRight = rightScores.reduce((a, b) => a + b, 0) / rightScores.length;
  return avgLeft >= avgRight;
}

/** Max rows per column in TOP/BOTTOM split halves — keeps every column the same height. */
export const HEATMAP_SPLIT_MAX_ROWS = 10;

/** Default columns per half when pool > 100 (5×10 = 50 tiles per side). */
export const HEATMAP_SPLIT_HALF_COLS = 5;

/** Lists this size or larger use a fixed 10-row grid; column count is derived from stock count. */
export const HEATMAP_ROW_LOCK_MIN_COUNT = 50;

/** Column count for a fixed-row grid (column-major ranking). */
export function heatmapColsForStockCount(
  stockCount: number,
  maxRows = HEATMAP_SPLIT_MAX_ROWS,
): number {
  if (stockCount <= 0) return HEATMAP_GRID_COLS;
  return Math.max(1, Math.ceil(stockCount / Math.max(1, maxRows)));
}

export function heatmapNarrowSingleGridMaxWidth(
  cols: number,
  flowRow: boolean,
  isSplitView: boolean,
): number | undefined {
  if (isSplitView || flowRow || cols <= 0) return undefined;
  if (cols in HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_BY_COLS) {
    return HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_BY_COLS[cols];
  }
  if (cols < 5) return HEATMAP_NARROW_SINGLE_GRID_MAX_WIDTH_FEW_COLS;
  return undefined;
}

export function heatmapNarrowSingleGridMaxWidthForStockCount(
  stockCount: number | null | undefined,
): number | undefined {
  if (stockCount == null || stockCount < HEATMAP_ROW_LOCK_MIN_COUNT) return undefined;
  return heatmapNarrowSingleGridMaxWidth(heatmapColsForStockCount(stockCount), false, false);
}

/** True when `cols` fit at the minimum tile width inside `availableWidth`. */
export function heatmapGridFitsAtMinWidth(
  cols: number,
  availableWidth: number,
  minCellW = HEATMAP_CELL_MIN_W,
): boolean {
  if (availableWidth <= 0 || cols <= 0) return true;
  return heatmapGridSpanWidth(cols, HEATMAP_CELL_GAP, minCellW) <= availableWidth + 0.5;
}

/** Single-grid cap — never grow past maxRows; drop rightmost columns' stocks when narrow. */
export function heatmapSingleGridDisplayCount(
  stockCount: number,
  cols: number,
  maxRows = HEATMAP_SPLIT_MAX_ROWS,
): number {
  if (cols <= 0) return stockCount;
  return Math.min(stockCount, cols * Math.max(1, maxRows));
}

/** Expected tile count for skeleton placeholders before the first RPC lands. */
export function heatmapEstimatedSkeletonCount(
  stocksCount: number | null | undefined,
  topByMarketCap: string,
): number {
  const trimmed = topByMarketCap.trim();
  const cap = trimmed ? parseInt(trimmed, 10) : NaN;
  const raw = stocksCount ?? (Number.isFinite(cap) && cap > 0 ? cap : null) ?? 100;
  return Math.min(Math.max(raw, 1), 100);
}

/** Universe size before RPC — list metadata + cap controls. */
export function heatmapExpectedPoolCount(
  stocksCount: number | null | undefined,
  topByMarketCap: string,
  listStockCount: number,
): number {
  const trimmed = topByMarketCap.trim();
  const cap = trimmed ? parseInt(trimmed, 10) : NaN;
  if (stocksCount != null && stocksCount > 0) return stocksCount;
  if (Number.isFinite(cap) && cap > 0) return cap;
  return listStockCount > 0 ? listStockCount : 100;
}

/**
 * Picker/RPC count for the *current* list only.
 * Never fall through to a leftover `listTotalCount` from the previous fetch —
 * that sized All Portfolios skeletons as USA 100 (or vice versa) and flashed
 * single-grid → TOP/BOTTOM when the new RPC landed.
 */
export function heatmapCurrentListStockCount(
  pickerCount: number,
  loadedListKey: string | null | undefined,
  selectedList: string,
  listTotalCount: number,
): number {
  if (pickerCount > 0) return pickerCount;
  if (loadedListKey === selectedList && listTotalCount > 0) return listTotalCount;
  return 0;
}

/**
 * Pool size used to lay out the loading skeleton.
 * When formula / metric-range filters will shrink the universe, ignore the
 * raw list size so All Portfolios + a Net Income floor does not flash a
 * 100-tile TOP/BOTTOM grid that then collapses.
 */
export function heatmapSkeletonPoolCount(
  stocksCount: number | null | undefined,
  topByMarketCap: string,
  listStockCount: number,
  universeWillNarrow: boolean,
): number {
  return heatmapExpectedPoolCount(
    stocksCount,
    topByMarketCap,
    universeWillNarrow ? 0 : listStockCount,
  );
}

/** Whether the loaded grid will use TOP/BOTTOM split (before data arrives). */
export function heatmapPredictSplitView(
  expectedPoolCount: number,
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
): boolean {
  return expectedPoolCount > 100 && !isFinancialsMetricCategory(metric, columnMeta);
}

/** Tile count for skeleton layout — mirrors {@link sliceHeatmapDisplayRows} caps. */
export function heatmapSkeletonDisplayCount(
  expectedPoolCount: number,
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
): number {
  if (heatmapPredictSplitView(expectedPoolCount, metric, columnMeta)) return 100;
  return Math.min(Math.max(expectedPoolCount, 1), 100);
}

/** Pixel height of a column-major grid block (rows × tile height + gaps). */
export function heatmapGridBlockHeight(
  rowCount: number,
  gap = HEATMAP_CELL_GAP,
  cellH = HEATMAP_CELL_H,
): number {
  if (rowCount <= 0) return 0;
  return rowCount * cellH + Math.max(0, rowCount - 1) * gap;
}

/** Reserved min-height for the heatmap grid area — prevents layout jump on load. */
export function heatmapGridMinHeight(
  layout: HeatmapGridLayoutConfig,
  displayCount: number,
  isSplit: boolean,
): number {
  const labelBlock = isSplit ? 30 : 0;
  const stackedGap = isSplit && layout.splitStacked ? 20 : 0;

  if (isSplit) {
    const halfLen = heatmapSplitHalfLen(layout.splitCols, displayCount);
    const rowsPerHalf = Math.min(
      HEATMAP_SPLIT_MAX_ROWS,
      Math.max(1, Math.ceil(halfLen / Math.max(1, layout.splitCols))),
    );
    const blockH = heatmapGridBlockHeight(rowsPerHalf);
    if (layout.splitStacked) {
      return labelBlock + blockH + stackedGap + labelBlock + blockH;
    }
    return labelBlock + blockH;
  }

  const capped = heatmapSingleGridDisplayCount(displayCount, layout.cols);
  const rowCount = layout.flowRow
    ? Math.max(1, Math.ceil(capped / Math.max(1, layout.cols)))
    : heatmapSingleGridRowCount(capped, layout.cols, layout.lockMaxRows);
  return heatmapGridBlockHeight(rowCount);
}

export function heatmapSingleGridRowCount(
  displayCount: number,
  cols: number,
  lockMaxRows = false,
  maxRows = HEATMAP_SPLIT_MAX_ROWS,
): number {
  if (cols <= 0) return 1;
  const rowCap = Math.max(1, maxRows);
  if (lockMaxRows) return rowCap;
  return Math.min(
    rowCap,
    Math.max(1, Math.ceil(displayCount / cols)),
  );
}

/**
 * Tiles per half in split view (TOP N / BOTTOM N).
 * Capped at `splitCols × maxRows` (and at maxHalf for the >100-stock path).
 * Also capped at `floor(totalStocks / 2)` so a width-driven split of a
 * sub-100 list never overlaps the two halves (no stock shown twice).
 * Always a multiple of splitCols so every column has the same row count.
 */
export function heatmapSplitHalfLen(
  splitCols: number,
  totalStocks = Number.POSITIVE_INFINITY,
  maxHalf = 50,
  maxRows = HEATMAP_SPLIT_MAX_ROWS,
): number {
  const cap = Math.min(
    maxHalf,
    splitCols * Math.max(1, maxRows),
    Math.floor(totalStocks / 2),
  );
  return Math.max(splitCols, Math.floor(cap / splitCols) * splitCols);
}

const HEATMAP_SPLIT_SEPARATOR_PX = 32;

/** Pixel width of one split half, or both halves side-by-side (+ separator). */
export function heatmapSplitGridSpanWidth(
  splitCols: number,
  splitStacked = false,
  gap = HEATMAP_CELL_GAP,
  cellW = HEATMAP_CELL_W,
): number {
  const half = heatmapGridSpanWidth(splitCols, gap, cellW);
  return splitStacked ? half : 2 * half + HEATMAP_SPLIT_SEPARATOR_PX;
}

/** How many columns fit in the available width (min 3, max 10). */
export function heatmapColsForWidth(
  availableWidth: number,
  maxCols = HEATMAP_GRID_COLS,
  cellW = HEATMAP_CELL_W,
): number {
  if (availableWidth <= 0) return maxCols;
  const span = cellW + HEATMAP_CELL_GAP;
  return Math.max(3, Math.min(maxCols, Math.floor((availableWidth + HEATMAP_CELL_GAP) / span)));
}

/** Pixel width of N columns + gaps at a given tile width. */
export function heatmapGridSpanWidth(
  cols: number,
  gap = HEATMAP_CELL_GAP,
  cellW = HEATMAP_CELL_W,
): number {
  return cols * cellW + Math.max(0, cols - 1) * gap;
}

/** CSS grid column tracks — minmax(0, …) stops nowrap cell text from blowing past the track width. */
export function heatmapGridColumnTracks(
  cols: number,
  cellW: number,
  opts?: { stretch?: boolean; isMobile?: boolean },
): string {
  if (opts?.stretch) {
    return `repeat(${cols}, minmax(${HEATMAP_CELL_W}px, 1fr))`;
  }
  return `repeat(${cols}, minmax(0, ${cellW}px))`;
}

/** Tile width so `cols` fit in `availableWidth` — grows above 103px when slack, shrinks when tight. */
export function heatmapCellWidthForCols(
  availableWidth: number,
  cols: number,
  gap = HEATMAP_CELL_GAP,
  minCellW = HEATMAP_CELL_MIN_W,
  maxCellW = Number.POSITIVE_INFINITY,
): number {
  if (availableWidth <= 0 || cols <= 0) return HEATMAP_CELL_W;
  const gaps = Math.max(0, cols - 1) * gap;
  const fitW = Math.floor((availableWidth - gaps) / cols);
  let w = fitW >= HEATMAP_CELL_W ? fitW : Math.max(minCellW, fitW);
  if (Number.isFinite(maxCellW) && maxCellW > 0) {
    w = Math.min(w, maxCellW);
  }
  return w;
}

/** True when the grid should grow tiles to consume leftover horizontal space. */
export function heatmapGridShouldStretch(
  availableWidth: number,
  cols: number,
  flowRow: boolean,
  gap = HEATMAP_CELL_GAP,
): boolean {
  if (flowRow || availableWidth <= 0 || cols <= 0) return false;
  return heatmapGridSpanWidth(cols, gap, HEATMAP_CELL_W) < availableWidth - 1;
}

/** Extra inset on panel shell — matches Custom `effectiveGridWidth + 26`. */
export const HEATMAP_PANEL_SHELL_EXTRA = 26;

/** Desktop panel width — 10-col heatmap grid + shell padding (aligns Custom + Reports). */
export const HEATMAP_DESKTOP_PANEL_WIDTH =
  heatmapGridSpanWidth(HEATMAP_GRID_COLS) + HEATMAP_PANEL_SHELL_EXTRA;

/** Width style for heatmap hero + visualization panels on desktop. */
export function heatmapDesktopPanelShellStyle(isMobile: boolean): CSSProperties {
  return {
    width: isMobile ? "100%" : HEATMAP_DESKTOP_PANEL_WIDTH,
    maxWidth: "100%",
  };
}

/** Full-width shell for Report mode — fills the content column like Custom. */
export function heatmapReportPanelShellStyle(): CSSProperties {
  return {
    width: "100%",
    maxWidth: "100%",
  };
}

/**
 * Panel/card width from layout cols — not from fit-content (avoids ResizeObserver flicker).
 * Returns undefined when grid is fluid (flowRow / narrow).
 */
export function heatmapPanelShellWidth(
  layout: HeatmapGridLayoutConfig,
  isSplitView: boolean,
  containerWidth = 0,
  fixedTileWidth = false,
  isMobile = false,
  shellExtra = HEATMAP_PANEL_SHELL_EXTRA,
): string | undefined {
  if (layout.flowRow) return undefined;

  const maxCellW = Number.POSITIVE_INFINITY;

  if (isSplitView) {
    const layoutCellW = heatmapLayoutCellWidth(isMobile);
    if (containerWidth > 0 && !fixedTileWidth) {
      return `${containerWidth + shellExtra}px`;
    }
    return `${heatmapSplitGridSpanWidth(layout.splitCols, layout.splitStacked, HEATMAP_CELL_GAP, layoutCellW) + shellExtra}px`;
  }

  if (containerWidth > 0) {
    if (!fixedTileWidth && heatmapGridShouldStretch(containerWidth, layout.cols, false)) {
      // Tiles stretch to fill the full container — the panel always reaches both
      // edges of its content column instead of hugging a capped grid span.
      return `${containerWidth + shellExtra}px`;
    }
    if (!fixedTileWidth && layout.lockMaxRows) {
      const cellW = heatmapCellWidthForCols(containerWidth, layout.cols, HEATMAP_CELL_GAP, HEATMAP_CELL_W, maxCellW);
      return `${heatmapGridSpanWidth(layout.cols, HEATMAP_CELL_GAP, cellW) + shellExtra}px`;
    }
  }

  const layoutCellW = heatmapLayoutCellWidth(isMobile);
  return `${heatmapGridSpanWidth(layout.cols, HEATMAP_CELL_GAP, layoutCellW) + shellExtra}px`;
}

export type HeatmapGridLayoutConfig = {
  cols: number;
  flowRow: boolean;
  splitCols: number;
  splitStacked: boolean;
  /**
   * True when a sub-100 list does not fit in `cols × 10` rows and must render
   * as a TOP / BOTTOM split (instead of dropping the overflowing columns).
   * The data-driven >100 split uses the caller's `isSplitView` flag instead.
   */
  widthSplit: boolean;
  /** Fixed 10 rows; columns derived from stock count (lists ≥ {@link HEATMAP_ROW_LOCK_MIN_COUNT}). */
  lockMaxRows: boolean;
  compactGap: boolean;
  /** Row flow / narrow panels — fluid tile width + tighter cell content. */
  compactCellLayout: boolean;
};

/**
 * Custom heatmap grid: column-major (top-to-bottom ranking) at all widths.
 *
 * Narrow panels drop columns one-by-one, with the row count capped at 10. When
 * the list is too long to fit in `cols × 10` rows we DO NOT truncate it —
 * instead we fall back to a TOP / BOTTOM split (`widthSplit`) so the highest and
 * lowest ranked stocks both stay visible. The data-driven >100-stock split is
 * still controlled by the caller's `isSplitView` flag.
 */
/**
 * A fixed heatmap format chosen by an author (Bastion Desk): exact columns, and
 * whether the grid shows TOP / BOTTOM halves. Rows come from `maxRows`.
 * Heatmap pages never pass it, so their adaptive layout is unchanged.
 */
export type HeatmapForcedLayout = { split: boolean; cols: number };

export function computeHeatmapGridLayout(
  containerWidth: number,
  isMobile: boolean,
  isSplitView: boolean,
  stockCount = 0,
  maxRows = HEATMAP_SPLIT_MAX_ROWS,
  forced?: HeatmapForcedLayout | null,
): HeatmapGridLayoutConfig {
  const splitSep = 32;
  const rowCap = Math.max(1, maxRows);

  if (forced && forced.cols > 0) {
    const cols = Math.max(1, Math.round(forced.cols));
    const isNarrowForced = isMobile || (containerWidth > 0 && containerWidth < 520);
    if (forced.split) {
      // Halves sit side by side while each still fits at the 80px tile floor.
      const halfMin = cols * HEATMAP_CELL_MIN_W + (cols - 1) * HEATMAP_CELL_GAP;
      return {
        cols,
        flowRow: false,
        splitCols: cols,
        splitStacked: isNarrowForced || (containerWidth > 0 && containerWidth < halfMin * 2 + splitSep),
        widthSplit: !isSplitView,
        lockMaxRows: false,
        compactGap: false,
        compactCellLayout: false,
      };
    }
    return {
      cols,
      flowRow: false,
      splitCols: 5,
      splitStacked: false,
      widthSplit: false,
      lockMaxRows: false,
      compactGap: false,
      compactCellLayout: false,
    };
  }

  const lockMaxRows =
    !isSplitView && stockCount >= HEATMAP_ROW_LOCK_MIN_COUNT;
  const rowLockedCols = lockMaxRows ? heatmapColsForStockCount(stockCount, rowCap) : 0;

  if (containerWidth <= 0 && !isMobile) {
    return {
      cols: lockMaxRows ? rowLockedCols : HEATMAP_GRID_COLS,
      flowRow: false,
      splitCols: 5,
      splitStacked: false,
      widthSplit: false,
      lockMaxRows,
      compactGap: false,
      compactCellLayout: false,
    };
  }

  const width = containerWidth || 360;
  const widthCols = heatmapColsForWidth(width, HEATMAP_GRID_COLS);
  const cols = lockMaxRows ? rowLockedCols : widthCols;
  const isNarrow = isMobile || (containerWidth > 0 && containerWidth < 520);
  const displayCols = lockMaxRows ? rowLockedCols : cols;

  // Width-driven split: too many rows for cols×maxRows, or grid does not fit at
  // nominal 103px tile width. Desktop 115px is a render floor when compressing —
  // not the threshold for abandoning a full table.
  const needWidthSplit =
    !isSplitView &&
    (stockCount > displayCols * rowCap ||
      !heatmapGridFitsAtMinWidth(displayCols, width, HEATMAP_CELL_W));

  if (isSplitView || needWidthSplit) {
    const halfBudget = Math.max(0, (width - splitSep) / 2);
    const minFullSplitWidth =
      2 * heatmapGridSpanWidth(HEATMAP_SPLIT_HALF_COLS) + splitSep;
    // >100 lists: keep 5×10 per half on desktop — do not shrink the panel
    // into a feedback loop that drops to 3 cols × 10 (60 tiles total).
    const splitCols =
      isSplitView && !isNarrow && width >= minFullSplitWidth
        ? HEATMAP_SPLIT_HALF_COLS
        : heatmapColsForWidth(halfBudget, HEATMAP_SPLIT_HALF_COLS);
    let splitStacked: boolean;
    if (isNarrow) {
      splitStacked = true;
    } else {
      const minSideBySide =
        splitCols * HEATMAP_CELL_W + (splitCols - 1) * HEATMAP_CELL_GAP;
      splitStacked = containerWidth < minSideBySide * 2 + splitSep;
    }
    // Each half stays column-major (top-to-bottom ranking); when too narrow to
    // sit side by side, the two halves stack vertically (splitStacked) instead
    // of flipping to row-major flow.
    return {
      cols,
      flowRow: false,
      splitCols,
      splitStacked,
      widthSplit: needWidthSplit,
      lockMaxRows: lockMaxRows && !needWidthSplit,
      compactGap: false,
      compactCellLayout: false,
    };
  }

  // Desktop: always keep vertical (column-major) ordering — only the column
  // count shrinks as the panel narrows. Never flip to row-major (horizontal),
  // which scrambles the top-to-bottom ranking and confuses readers.
  // Lists ≥50 keep 10 rows and derive column count from stock count instead.
  return {
    cols,
    flowRow: false,
    splitCols: 5,
    splitStacked: false,
    widthSplit: false,
    lockMaxRows,
    compactGap: false,
    compactCellLayout: false,
  };
}

/** Vertical space between report sheets (replaces removed hairline dividers). */
export const HEATMAP_REPORT_SHEET_GAP = 36;

/** Tailwind classes for heatmap tile inner layout (Custom + Metric Report). */
export function heatmapCellLayoutClasses(compactCellLayout: boolean): string {
  return compactCellLayout
    ? "flex flex-col justify-start gap-0.5 h-full min-h-0 max-h-full min-w-0 max-w-full py-1 pl-1.5 pr-1.5 overflow-hidden"
    : "flex flex-col justify-between h-full min-w-0 max-w-full overflow-hidden py-1.5 pl-1.5 pr-1.5";
}

export function mainHeatmapGridStyle(
  cellCount: number,
  cols = HEATMAP_GRID_COLS,
  lockMaxRows = cellCount >= HEATMAP_ROW_LOCK_MIN_COUNT,
): CSSProperties {
  const displayCount = heatmapSingleGridDisplayCount(cellCount, cols);
  const rowCount = heatmapSingleGridRowCount(displayCount, cols, lockMaxRows);
  return {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, ${HEATMAP_CELL_W}px)`,
    gridAutoRows: `${HEATMAP_CELL_H}px`,
    gridTemplateRows: `repeat(${rowCount}, ${HEATMAP_CELL_H}px)`,
    gap: HEATMAP_CELL_GAP,
    columnGap: HEATMAP_CELL_GAP,
    rowGap: HEATMAP_CELL_GAP,
    gridAutoFlow: "column",
  };
}

/** Compact 500 tab — 10 × 50 small tiles (Custom + report panels). */
/** Max width — grid is fluid below this; wide screens get wider tiles. */
export const COMPACT500_GRID_WIDTH = 1400;
export const COMPACT500_TILE_HEIGHT = 19;
export const COMPACT500_MAX_ROWS = 500;
export const COMPACT500_COLS = 10;
export const PAYWALL_TILE_BG = "#2e2e2e";

export type Compact500StyleContext = {
  metric: string;
  columnMeta: Record<string, ColumnMetaLite>;
  finScoreMap: Map<string, number> | null;
  scoreKey: string | null;
  isStrategy: boolean;
  isMarketCap: boolean;
  isReturnMetric: boolean;
  total: number;
  posMin: number;
  posMax: number;
  negMin: number;
  negMax: number;
};

export function prepareCompact500Rows(
  stocks: Row[],
  metric: string,
  options: PrepareHeatmapStockPoolOptions & { sortAscending: boolean },
): Row[] {
  const hasLimit = options.stocksCount != null && options.topByMarketCap !== "";
  const pool = prepareHeatmapStockPool(stocks, metric, options);
  const sorted = sortHeatmapPoolByMetric(pool, metric, options.sortAscending);
  return hasLimit ? sorted : sorted.slice(0, COMPACT500_MAX_ROWS);
}

export function buildCompact500StyleContext(
  rows: Row[],
  metric: string,
  columnMeta: Record<string, ColumnMetaLite>,
): Compact500StyleContext {
  const keys = rows[0] ? Object.keys(rows[0]) : [];
  const isStrategy = isStrategyMetric(metric);
  const isMarketCap = isMarketCapMetric(metric);
  const isReturnMetric = isSignedReturnColorMetric(metric, columnMeta);
  const cat = columnMeta[metric]?.category;
  const isFinancials = cat === "Financials" || cat === "Forecast";
  const finScoreMap = isFinancials ? buildFinancialsScoreMap(rows, metric) : null;
  const scoreKey =
    !isStrategy && !isMarketCap && !isFinancials ? scoreFieldFor(keys, metric) : null;

  let posMin = Infinity;
  let posMax = -Infinity;
  let negMin = Infinity;
  let negMax = -Infinity;
  if (isReturnMetric) {
    for (const row of rows) {
      const v = asNum(row[metric]);
      if (v != null && isFinite(v) && v !== 0) {
        if (v > 0) {
          if (v < posMin) posMin = v;
          if (v > posMax) posMax = v;
        } else {
          if (v < negMin) negMin = v;
          if (v > negMax) negMax = v;
        }
      }
    }
  }

  return {
    metric,
    columnMeta,
    finScoreMap,
    scoreKey,
    isStrategy,
    isMarketCap,
    isReturnMetric,
    total: rows.length,
    posMin,
    posMax,
    negMin,
    negMax,
  };
}

function compact500DbScore(row: Row, ctx: Compact500StyleContext): number | null {
  if (ctx.isStrategy || ctx.isMarketCap) return asNum(row[ctx.metric]);
  if (ctx.finScoreMap) return ctx.finScoreMap.get(row.FullTicker as string) ?? null;
  return ctx.scoreKey ? asNum(row[ctx.scoreKey]) : null;
}

/** Compact 500 tile colors — same rules as the Custom Compact 500 tab. */
export function resolveCompact500CellStyle(
  row: Row,
  index: number,
  hasValid: boolean,
  rawValue: number | null,
  ctx: Compact500StyleContext,
): HeatmapCellStyle {
  if (!hasValid) return heatmapStyleByScore(null);

  let score = compact500DbScore(row, ctx);

  if (
    ctx.isReturnMetric &&
    rawValue !== null &&
    isFinite(rawValue) &&
    score !== null
  ) {
    if (rawValue > 0 && score < 4) score = 4;
    else if (rawValue < 0 && score >= 4) score = 3;
  }

  if (score === null) {
    if (ctx.isReturnMetric && rawValue !== null && isFinite(rawValue)) {
      if (rawValue > 0) {
        const t =
          ctx.posMax > ctx.posMin
            ? (rawValue - ctx.posMin) / (ctx.posMax - ctx.posMin)
            : 0.5;
        score = 4 + t * 6;
      } else if (rawValue < 0) {
        const t =
          ctx.negMax > ctx.negMin
            ? (rawValue - ctx.negMin) / (ctx.negMax - ctx.negMin)
            : 0.5;
        score = 1 + t * 2;
      } else {
        score = 5;
      }
    } else if (ctx.total > 1) {
      const t = index / (ctx.total - 1);
      score = 10 - t * 9;
    } else {
      score = 5;
    }
  }

  if (ctx.isStrategy && score !== null) {
    return heatmapStyleByStrategyScore(score);
  }

  return heatmapStyleByScore(score);
}
